/**
 * AddFirstLeadModal — Iter 20.
 *
 * Minimal paste/form modal for the Getting Started checklist's
 * "Add your first lead" step. Captures name + email + optional company,
 * POSTs /api/leads, then notifies the checklist to mark the step done.
 *
 * The full guided onboarding (paste / connect / CSV order) is parked for
 * Tier 2. This is the minimum unblock for completing the step today.
 */
import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, UserPlus } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { toast } from "sonner";

export function AddFirstLeadModal({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setName(""); setEmail(""); setCompany(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() && !email.trim()) {
      toast.error("Add at least a name or an email");
      return;
    }
    setSaving(true);
    try {
      await api.post("/leads", {
        name: name.trim() || email.split("@")[0] || "Unnamed lead",
        email: email.trim() || null,
        company: company.trim() || null,
        source: "getting_started",
      });
      toast.success("Lead added");
      reset();
      onCreated?.();
    } catch (err) {
      toast.error(errorMessage(err) || "Could not add lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent
        className="border-white/10 bg-[#0a0a0a] text-white sm:max-w-md"
        data-testid="add-first-lead-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-2xl">
            <UserPlus className="h-5 w-5 text-violet-300" /> Add your first lead
          </DialogTitle>
          <DialogDescription className="text-white/55">
            Paste in a single contact to get started. You can import the rest from CSV later.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/50">Full name</Label>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              className="border-white/10 bg-black/40 text-white"
              data-testid="afl-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/50">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@acme.com"
              className="border-white/10 bg-black/40 text-white"
              data-testid="afl-email"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/50">Company <span className="text-white/30">(optional)</span></Label>
            <Input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Acme Inc."
              className="border-white/10 bg-black/40 text-white"
              data-testid="afl-company"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-white/65"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || (!name.trim() && !email.trim())}
              className="rounded-xl bg-white text-black hover:bg-zinc-200"
              data-testid="afl-submit"
            >
              {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              Add lead
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
